import { UsersAndUserGroupsDataModel } from "../UserGroupsManagement/userGroupsManagement";
import { HubItemCategoryModel } from "../Hub/hub";
import { environmentHttpClient } from "../httpClient";
import { HubItemAccessModel } from "../Announcements/announcements";
import { TagSaveModel, TagViewModel } from "client/Api/Tags/tags";
import {
	HubItemLayoutSaveModel,
	HubItemLayoutComposerViewModel,
	HubLinkedDocumentSaveModel,
	HubItemMediaComposerViewModel,
	HubItemMediaSaveModel,
	HubHierarchyListNodeModel
} from "../Hub/common.composer";
import { KnowledgeItemPinModel } from "../Hub/KnowledgeItems/knowledgeItemViewer";
import { HubItemImageRestrictionsModel, HubLinkedDocumentViewModel } from "../Hub/common";
import { UserIdentityModel } from "../UserProfile/service";
import { DocumentLocationViewModel } from "../Hub/hubSettings";

export enum KnowledgeItemStatus {
	Draft = 1,
	Published = 2,
	Archived = 3
}

export type KnowledgeItemSaveModel = {
	title: string;
	layout: HubItemLayoutSaveModel;
	media: HubItemMediaSaveModel;
	showAuthor: boolean;
	isForEveryoneInTheCategory: boolean;
	access: HubItemAccessModel | null;
	categoryId: number | null;
	tags: readonly TagSaveModel[];
	linkedDocuments: readonly HubLinkedDocumentSaveModel[];
	isPinned: boolean;
	areCommentsEnabled: boolean;
};

export type CheckPinLimitResult = {
	canBePinned: boolean;
	pinnedItems: KnowledgeItemPinModel[];
};

export type EditKnowledgeItemPageViewModel = {
	knowledgeItem: KnowledgeItemViewModel;
	hasPermissionToCreateNewCategory: boolean;
	usersAndUserGroupsData: UsersAndUserGroupsDataModel;
	knowledgeItemHierarchy: HubHierarchyListNodeModel[];
	imageRestrictions: HubItemImageRestrictionsModel;
	defaultImageLocation: DocumentLocationViewModel | null;
	createdBy: UserIdentityModel | null;
	publicationDate: Date | string | null;
	isPixabayImageGalleryAvailable: boolean;
};

export type KnowledgeItemViewModel = {
	id: number;
	title: string | null;
	layout: HubItemLayoutComposerViewModel | null;
	media: HubItemMediaComposerViewModel;
	showAuthor: boolean;
	isForEveryoneInTheCategory: boolean;
	access: HubItemAccessModel | null;
	categoryId: number | null;
	status: KnowledgeItemStatus;
	tags: readonly TagViewModel[] | null;
	linkedDocuments: readonly HubLinkedDocumentViewModel[] | null;
	isPinned: boolean;
	areCommentsEnabled: boolean;
};

export type KnowledgeItemSaveRequestModel = {
	knowledgeItemSaveModel: KnowledgeItemSaveModel;
	shouldPublishItem: boolean;
	shouldSendNotifications: boolean | null;
};

export type KnowledgeItemSaveResult = {
	knowledgeItemId: number;
};

export type KnowledgeItemListItemModel = {
	id: number;
	title: string;
	previewText: string;
	publicationDate: Date | string | null;
	lastModifiedDate: Date | string;
	createdByUserName: string;
	lastModifiedByUserName: string;
	viewsCount: number;
	commentsCount: number;
	reactionsCount: number;
	categoryName: string;
	categoryId: number | null;
	status: KnowledgeItemStatus;
	tags: readonly TagViewModel[];
	isPinned: boolean;
};

export type KnowledgeItemListModel = {
	knowledgeItems: readonly KnowledgeItemListItemModel[];
	categories: readonly HubItemCategoryModel[];
	canCreateTemplates: boolean;
};

export function createKnowledgeItem(
	model: KnowledgeItemSaveRequestModel
): Promise<KnowledgeItemSaveResult> {
	return environmentHttpClient.post<KnowledgeItemSaveResult>(
		"UiApi/KnowledgeItemManagement",
		model
	);
}

export function updateKnowledgeItem(
	id: number,
	model: KnowledgeItemSaveRequestModel
): Promise<KnowledgeItemSaveResult> {
	return environmentHttpClient.put<KnowledgeItemSaveResult>(
		`UiApi/KnowledgeItemManagement/${id}`,
		model
	);
}

export function getNewKnowledgeItemModel(
	itemToCopyId: undefined,
	templateId: undefined,
	signal?: AbortSignal
): Promise<EditKnowledgeItemPageViewModel>;
export function getNewKnowledgeItemModel(
	itemToCopyId: number,
	templateId: undefined,
	signal?: AbortSignal
): Promise<EditKnowledgeItemPageViewModel>;
export function getNewKnowledgeItemModel(
	itemToCopyId: undefined,
	templateId: number,
	signal?: AbortSignal
): Promise<EditKnowledgeItemPageViewModel>;
export function getNewKnowledgeItemModel(
	itemToCopyId: number | undefined,
	templateId: number | undefined,
	signal?: AbortSignal
): Promise<EditKnowledgeItemPageViewModel> {
	return environmentHttpClient.get<EditKnowledgeItemPageViewModel>(
		"UiApi/KnowledgeItemManagement/Add",
		{ itemToCopyId, templateId },
		{ signal }
	);
}

export function getEditKnowledgeItemModel(id: number, signal?: AbortSignal): Promise<EditKnowledgeItemPageViewModel> {
	return environmentHttpClient.get<EditKnowledgeItemPageViewModel>(
		`UiApi/KnowledgeItemManagement/Edit/${id}`,
		undefined,
		{ signal }
	);
}

export function deleteKnowledgeItems(ids: number[], signal?: AbortSignal): Promise<void> {
	return environmentHttpClient.delete<void>(
		"UiApi/KnowledgeItemManagement",
		ids,
		{ signal }
	);
}

export function listKnowledgeItems(
	signal?: AbortSignal
): Promise<KnowledgeItemListModel> {
	return environmentHttpClient.get<KnowledgeItemListModel>(
		"UiApi/KnowledgeItemManagement/List",
		undefined,
		{ signal }
	);
}

export function archiveKnowledgeItems(
	ids: number[],
	signal?: AbortSignal
): Promise<void> {
	return environmentHttpClient.put<void>(
		"UiApi/KnowledgeItemManagement/Archive",
		ids,
		{ signal }
	);
}

export function checkPinLimit(
	categoryId: number,
	knowledgeItemId: number,
	signal?: AbortSignal
): Promise<CheckPinLimitResult> {
	return environmentHttpClient.get<CheckPinLimitResult>(
		`UiApi/KnowledgeItemManagement/CheckPinLimit`,
		{categoryId, knowledgeItemId},
		{ signal }
	);
}

export function unPin(
	ids: number[],
	signal?: AbortSignal
): Promise<void> {
	return environmentHttpClient.put<void>(
		"UiApi/KnowledgeItemManagement/unPin",
		ids,
		{ signal }
	);
}
